import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './DailyReport.css';
import config from '../config';
import { ceilPercentUi, ceilNumberUi } from '../utils/ceilDisplay';
import { Box, Card, CardContent, Grid, Typography, Divider, Tabs, Tab } from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AlarmIcon from '@mui/icons-material/Alarm';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningIcon from '@mui/icons-material/Warning';
import ProductionQuantityLimitsIcon from '@mui/icons-material/ProductionQuantityLimits';

const DailyReport = (props) => {
  // Get yesterday's date
  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };
  
  // Get today's date
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };
  
  // Format current date as DD-MM-YYYY (PHP-like format)
  const getCurrentDateFormatted = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  };
  
  const [selectedDate, setSelectedDate] = useState(getTodayDate()); // Date field shows today
  const [dateRangeType, setDateRangeType] = useState('daterange'); // Default to date range to use from/to dates
  const [fromDate, setFromDate] = useState(getYesterdayDate()); // Default: previous date
  const [toDate, setToDate] = useState(getYesterdayDate()); // Default: previous date (same as from)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMachine, setSelectedMachine] = useState('all');
  const [selectedMachineType, setSelectedMachineType] = useState('all');
  const [selectedOperator, setSelectedOperator] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [dashboardTab, setDashboardTab] = useState(0); // Tab state for Dashboard tabs (0 = Machine Chart, 1 = Summary)
  const [attendanceDashboardData, setAttendanceDashboardData] = useState(null);
  const [attendanceDashboardLoading, setAttendanceDashboardLoading] = useState(false);
  const [attendanceDashboardError, setAttendanceDashboardError] = useState(null);
  const [reportData, setReportData] = useState({
    totalQtyPacked: 0,
    totalQtyProduced: 0,
    totalOperatorsPresent: 0,
    totalAbsent: 0,
    totalOperatorHrs: 0,
    totalOTHrs: 0,
    machineData: []
  });
  const [totals, setTotals] = useState({
    completedQty: 0,
    rejectedQty: 0,
    totalTimeSpent: 0,
    ot: 0,
    machineDownTime: 0,
    maintenanceTime: 0
  });

  useEffect(() => {
    fetchReportData();
  }, [selectedDate, dateRangeType, fromDate, toDate]);

  // Fetch attendance dashboard data when Summary tab is active
  useEffect(() => {
    if (dashboardTab === 1) {
      fetchAttendanceDashboardData();
    }
  }, [dashboardTab]);

  // Calculate date range based on selection
  const getDateParams = () => {
    const today = new Date();
    let calculatedFromDate, calculatedToDate;

    switch (dateRangeType) {
      case 'today':
        // When 'today' is selected, use yesterday's date for the API call
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        calculatedFromDate = calculatedToDate = yesterday.toISOString().split('T')[0];
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
        calculatedFromDate = weekStart.toISOString().split('T')[0];
        calculatedToDate = new Date().toISOString().split('T')[0];
        break;
      case 'month':
        calculatedFromDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        calculatedToDate = new Date().toISOString().split('T')[0];
        break;
      case 'daterange':
        calculatedFromDate = fromDate;
        calculatedToDate = toDate;
        break;
      default:
        calculatedFromDate = calculatedToDate = selectedDate;
    }

    return { from_date: calculatedFromDate, to_date: calculatedToDate };
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const dateParams = getDateParams();
      console.log('Fetching data with params:', dateParams);
      
      // Build request body based on date range type
      // For 'today', send only date. For others, send from_date and to_date
      let requestBody = {};
      if (dateRangeType === 'today') {
        requestBody = { date: selectedDate };
      } else {
        requestBody = {
          from_date: dateParams.from_date,
          to_date: dateParams.to_date
        };
      }
      
      console.log('Request body:', requestBody);
      
      // Try POST request instead of GET (API returns 405 for GET)
      const response = await axios.post(`${config.apiUrl}/getdailyreport`, requestBody, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      });

      console.log('✅ API Response received:', response.data);

      if (response.data && response.data.status === 'success') {
        // Map API response to component state
        const summary = response.data.summary && response.data.summary[0] ? response.data.summary[0] : {};
        const machineProduction = response.data.machine_wise_production || [];
        const apiTotals = response.data.totals && response.data.totals[0] ? response.data.totals[0] : {};

        console.log('📊 Summary data:', summary);
        console.log('🏭 Machine production count:', machineProduction.length);

        const mappedData = {
          totalQtyPacked: Math.ceil(Number(summary.total_qty_sent_to_fg) || 0),
          totalQtyProduced: Math.ceil(Number(summary.total_qty_produced) || 0),
          totalOperatorsPresent: Math.ceil(Number(summary.total_operators_present) || 0),
          totalAbsent: Math.ceil(Number(summary.total_absent) || 0),
          totalOperatorHrs: Math.ceil(Number(summary.total_operator_hrs) || 0),
          totalOTHrs: Math.ceil(Number(summary.total_ot_hrs) || 0),
          machineData: machineProduction.map(item => ({
            date: item.date,
            machineId: item.machine_id,
            machineType: item.machine_type,
            part: item.part,
            process: item.process,
            operator: item.operator,
            employeeName: item.employee_name || item.employeeName || item.operator || '-',
            completedQty: Math.ceil(item.completed_qty || 0),
            producedQty: Math.ceil(item.produced_qty || item.completed_qty || 0),
            rejectedQty: Math.ceil(item.rejected_qty || 0),
            startTime: item.start_time,
            endTime: item.end_time,
            totalTimeSpent: Math.ceil(item.time_spent || 0),
            ot: Math.ceil(item.ot || 0),
            machineDownTime: Math.ceil(item.machine_down || 0),
            maintenanceTime: Math.ceil(item.maintenance_time || 0)
          }))
        };

        console.log('✨ Mapped data:', mappedData);
        console.log('Card values will be:', {
          card1: mappedData.totalQtyPacked,
          card2: mappedData.totalQtyProduced,
          card3: mappedData.totalOperatorsPresent,
          card4: mappedData.totalAbsent,
          card5: mappedData.totalOperatorHrs,
          card6: mappedData.totalOTHrs
        });
        
        setReportData(mappedData);

        setTotals({
          completedQty: Math.ceil(apiTotals.completed_qty_total || 0),
          rejectedQty: Math.ceil(apiTotals.rejected_qty_total || 0),
          totalTimeSpent: Math.ceil(apiTotals.time_spent_total || 0),
          ot: Math.ceil(apiTotals.ot_total || 0),
          machineDownTime: Math.ceil(apiTotals.machine_down_total || 0),
          maintenanceTime: Math.ceil(apiTotals.maintenance_total || 0)
        });
        
        console.log('✅ State updated successfully!');
      } else {
        console.error('❌ API returned non-success status:', response.data);
        setError('API returned an error status');
        loadSampleData(); // Load sample data as fallback
      }
    } catch (err) {
      console.error('❌ Error fetching report data:', err);
      console.error('Error details:', err.response?.data || err.message);
      console.error('Request URL:', `${config.apiUrl}/getdailyreport`);
      console.error('Request params:', { date: selectedDate, ...getDateParams() });
      
      // More detailed error message
      let errorMessage = `Failed to fetch data from API: ${err.message}`;
      if (err.response) {
        errorMessage += ` (Status: ${err.response.status})`;
      } else if (err.request) {
        errorMessage += ' (Network Error - API may be unreachable)';
      }
      errorMessage += '. Loading sample data...';
      
      setError(errorMessage);
      loadSampleData(); // Load sample data as fallback
    } finally {
      setLoading(false);
    }
  };

  // Sample data fallback function
  const loadSampleData = () => {
    console.log('📦 Loading sample data as fallback...');
    const sampleData = {
      totalQtyPacked: 3250,
      totalQtyProduced: 3100,
      totalOperatorsPresent: 105,
      totalAbsent: 4,
      totalOperatorHrs: 850,
      totalOTHrs: 10,
      machineData: [
        { date: "2025-10-31", machineId: "1", machineType: "CNC", part: "A123", process: "Welding", operator: "John Doe", employeeName: "John Doe", completedQty: 150, producedQty: 160, rejectedQty: 5, startTime: "09:00", endTime: "17:30", totalTimeSpent: 8.5, ot: 1, machineDownTime: 0.5, maintenanceTime: 0 },
        { date: "2025-10-31", machineId: "2", machineType: "Lathe", part: "B456", process: "Cutting", operator: "Jane Smith", employeeName: "Jane Smith", completedQty: 200, producedQty: 205, rejectedQty: 2, startTime: "08:30", endTime: "17:00", totalTimeSpent: 8.5, ot: 0, machineDownTime: 0, maintenanceTime: 0.2 },
        { date: "2025-10-31", machineId: "3", machineType: "Assembly Line", part: "C789", process: "Assembly", operator: "Rahul", employeeName: "Rahul Kumar", completedQty: 180, producedQty: 185, rejectedQty: 3, startTime: "09:00", endTime: "17:00", totalTimeSpent: 8, ot: 1, machineDownTime: 0.3, maintenanceTime: 0 },
        { date: "2025-10-31", machineId: "4", machineType: "Paint Booth", part: "D234", process: "Painting", operator: "Sneha", employeeName: "Sneha Patel", completedQty: 220, producedQty: 225, rejectedQty: 4, startTime: "08:45", endTime: "17:15", totalTimeSpent: 8.5, ot: 0, machineDownTime: 0.2, maintenanceTime: 0.1 }
      ]
    };
    
    setReportData(sampleData);
    setTotals({
      completedQty: 750,
      rejectedQty: 14,
      totalTimeSpent: 33,
      ot: 2,
      machineDownTime: 1,
      maintenanceTime: 0.3
    });
    console.log('✅ Sample data loaded!');
  };

  const username = localStorage.getItem('username') || 'User';

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format time to HH:MM (remove seconds)
  const formatTime = (timeString) => {
    if (!timeString) return '';
    // If time is in HH:MM:SS format, split and take first two parts
    const timeParts = timeString.split(':');
    if (timeParts.length >= 2) {
      return `${timeParts[0]}:${timeParts[1]}`;
    }
    return timeString;
  };

  // Format name to sentence case (first letter uppercase, rest lowercase)
  const toSentenceCase = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Get unique machine IDs, machine types, operators, and names for filter dropdowns
  const uniqueMachines = ['all', ...new Set(reportData.machineData.map(item => item.machineId))];
  const uniqueMachineTypes = ['all', ...new Set(reportData.machineData.map(item => item.machineType).filter(Boolean))];
  const uniqueOperators = ['all', ...new Set(reportData.machineData.map(item => item.operator))];

  // Filter data based on selected filters
  const filteredData = reportData.machineData.filter(item => {
    const machineMatch = selectedMachine === 'all' || item.machineId === selectedMachine;
    const machineTypeMatch = selectedMachineType === 'all' || item.machineType === selectedMachineType;
    const operatorMatch = selectedOperator === 'all' || item.operator === selectedOperator;
    
    // Filter by status
    let statusMatch = true;
    if (selectedStatus === 'completed') {
      statusMatch = item.endTime && item.endTime.trim() !== '';
    } else if (selectedStatus === 'started') {
      statusMatch = !item.endTime || item.endTime.trim() === '';
    }
    
    // Filter by date range
    let dateMatch = true;
    if (item.date) {
      const itemDate = new Date(item.date);
      const dateParams = getDateParams();
      const fromDateObj = new Date(dateParams.from_date);
      const toDateObj = new Date(dateParams.to_date);
      
      // Set time to midnight for accurate date comparison
      itemDate.setHours(0, 0, 0, 0);
      fromDateObj.setHours(0, 0, 0, 0);
      toDateObj.setHours(0, 0, 0, 0);
      
      dateMatch = itemDate >= fromDateObj && itemDate <= toDateObj;
    }
    
    return machineMatch && machineTypeMatch && operatorMatch && statusMatch && dateMatch;
  });

  // Calculate totals for filtered data
  const filteredTotals = filteredData.reduce((acc, item) => ({
    completedQty: acc.completedQty + (Number(item.completedQty) || 0),
    producedQty: acc.producedQty + (Number(item.producedQty) || Number(item.completedQty) || 0),
    rejectedQty: acc.rejectedQty + (Number(item.rejectedQty) || 0),
    totalTimeSpent: acc.totalTimeSpent + (Number(item.totalTimeSpent) || 0),
    ot: acc.ot + (Number(item.ot) || 0)
  }), { completedQty: 0, producedQty: 0, rejectedQty: 0, totalTimeSpent: 0, ot: 0 });

  const isFilteredView = selectedMachine !== 'all' || selectedMachineType !== 'all' || selectedOperator !== 'all' || selectedStatus !== 'all';

  // Calculate summary cards from filtered data
  // Use API summary values when no filters are applied, otherwise calculate from filtered data
  // totalQtyPacked uses reportData.totalQtyPacked which comes from API's total_qty_sent_to_fg
  const filteredSummary = {
    totalQtyPacked: isFilteredView ? filteredTotals.completedQty : reportData.totalQtyPacked,
    totalQtyProduced: isFilteredView ? filteredTotals.producedQty : reportData.totalQtyProduced,
    totalOperatorsPresent: isFilteredView ? new Set(filteredData.map(item => item.operator)).size : reportData.totalOperatorsPresent,
    totalAbsent: isFilteredView ? 0 : reportData.totalAbsent, // This would need to come from a different data source when filtered
    totalOperatorHrs: isFilteredView ? filteredTotals.totalTimeSpent : reportData.totalOperatorHrs,
    totalOTHrs: isFilteredView ? filteredTotals.ot : reportData.totalOTHrs
  };

  // Fetch attendance dashboard data (uses previous date)
  const fetchAttendanceDashboardData = async () => {
    try {
      setAttendanceDashboardLoading(true);
      setAttendanceDashboardError(null);
      
      // Use previous date (yesterday) for the API call
      const previousDate = getYesterdayDate();
      const requestBody = { date: previousDate };
      
      console.log('Fetching attendance dashboard data for date:', previousDate);
      
      const response = await axios.post(
        'https://apir4prod.bsynapse.com/ifeccustomguard/attendancedashboard',
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      console.log('✅ Attendance Dashboard API Response:', response.data);
      console.log('✅ Full Response:', JSON.stringify(response.data, null, 2));

      // Handle different response formats
      if (response.data) {
        // Check if response has status field
        if (response.data.status === 'success' || response.data.status === undefined) {
          setAttendanceDashboardData(response.data);
        } else {
          throw new Error(`API returned status: ${response.data.status}`);
        }
      } else {
        throw new Error('API returned empty response');
      }
    } catch (err) {
      console.error('❌ Error fetching attendance dashboard data:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      let errorMessage = `Failed to load attendance dashboard data: ${err.message}`;
      if (err.response?.data) {
        errorMessage += ` - ${JSON.stringify(err.response.data)}`;
      }
      setAttendanceDashboardError(errorMessage);
      // Set empty data as fallback
      setAttendanceDashboardData(null);
    } finally {
      setAttendanceDashboardLoading(false);
    }
  };

  // Handle date range type change
  const handleDateRangeTypeChange = (e) => {
    const type = e.target.value;
    setDateRangeType(type);
    
    // Reset dates when changing type
    if (type === 'daterange') {
      // When switching to date range, set both to yesterday's date
      const yesterday = getYesterdayDate();
      setFromDate(yesterday);
      setToDate(yesterday);
    } else if (type !== 'daterange') {
      const today = getTodayDate();
      setFromDate(today);
      setToDate(today);
      // Keep selectedDate as today for the Date field display
      setSelectedDate(today);
    }
  };

  return (
    <div className="daily-report-container">
      <div className="report-header">
        <div className="header-left">
          <h1>Daily Production Report</h1>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
            As on {getCurrentDateFormatted()} 9am
          </p>
        </div>
        <div className="header-right">
          <div className="date-controls">
            <div className="date-range-type">
              <label>Period: </label>
              <select 
                value={dateRangeType} 
                onChange={handleDateRangeTypeChange}
                className="date-range-select"
              >
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="daterange">Date Range</option>
              </select>
            </div>
            
            {dateRangeType === 'daterange' ? (
              <div className="date-range-inputs">
                <div className="date-input-group">
                  <label>From: </label>
                  <input 
                    type="date" 
                    value={fromDate} 
                    onChange={(e) => setFromDate(e.target.value)}
                    className="date-input"
                  />
                </div>
                <div className="date-input-group">
                  <label>To: </label>
                  <input 
                    type="date" 
                    value={toDate} 
                    onChange={(e) => setToDate(e.target.value)}
                    className="date-input"
                  />
                </div>
              </div>
            ) : dateRangeType === 'today' ? (
              <div className="date-selector">
                <label>Date: </label>
                <input 
                  type="date" 
                  value={selectedDate} 
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="date-input"
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Summary Cards - Only show in first tab (Machine wise production chart) */}
      {dashboardTab === 0 && (
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: { 
          xs: '1fr',
          sm: '1fr', 
          lg: 'repeat(6, 1fr)' 
        },
        gap: 2,
        mb: 4
      }}>
        <Card 
          sx={{ 
            minHeight: '120px',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px) scale(1.02)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
            }
          }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Inventory2Icon sx={{ fontSize: '2.5rem', color: 'primary.main', mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.4 }}>
                    Total Qty Packed/ Sent To FG
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#000', fontSize: '2rem' }}>
                    {reportData.totalQtyPacked}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
        </Card>
        <Card 
          sx={{ 
            minHeight: '120px',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px) scale(1.02)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
            }
          }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <ProductionQuantityLimitsIcon sx={{ fontSize: '2.5rem', color: 'primary.main', mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.4 }}>
                    Total Qty Produced
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#000', fontSize: '2rem' }}>
                    {reportData.totalQtyProduced}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
        </Card>
        <Card 
          sx={{ 
            minHeight: '120px',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px) scale(1.02)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
            }
          }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <GroupsIcon sx={{ fontSize: '2.5rem', color: 'primary.main', mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.4 }}>
                    Total Operators Present
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#000', fontSize: '2rem' }}>
                    {reportData.totalOperatorsPresent}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
        </Card>
        <Card 
          sx={{ 
            minHeight: '120px',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px) scale(1.02)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
            }
          }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <PersonOffIcon sx={{ fontSize: '2.5rem', color: 'primary.main', mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.4 }}>
                    Total Absent
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#000', fontSize: '2rem' }}>
                    {reportData.totalAbsent}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
        </Card>
        <Card 
          sx={{ 
            minHeight: '120px',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px) scale(1.02)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
            }
          }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <AlarmIcon sx={{ fontSize: '2.5rem', color: 'primary.main', mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.4 }}>
                    Total ET hrs
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#000', fontSize: '2rem' }}>
                    {reportData.totalOTHrs}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
        </Card>
      </Box>
      )}

      {/* Dashboard Tabs */}
      <Box sx={{ width: '100%', mt: 3 }}>
        <Tabs 
          value={dashboardTab} 
          onChange={(e, newValue) => setDashboardTab(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 3,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              minHeight: '48px'
            }
          }}
        >
          <Tab label="Machine wise production chart" />
          <Tab label="Summary" />
        </Tabs>

        {/* Tab Panel 0: Machine wise production chart */}
        {dashboardTab === 0 && (
          <div className="table-section">
            <div className="table-header">
              <h2>Machine wise production chart</h2>
          <div className="filter-section">
            <div className="filter-group">
              <label>Machine ID:</label>
              <select 
                value={selectedMachine} 
                onChange={(e) => setSelectedMachine(e.target.value)}
                className="filter-select"
              >
                {uniqueMachines.map(machine => (
                  <option key={machine} value={machine}>
                    {machine === 'all' ? 'All Machines' : `Machine ${machine}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Machine Type:</label>
              <select 
                value={selectedMachineType} 
                onChange={(e) => setSelectedMachineType(e.target.value)}
                className="filter-select"
              >
                {uniqueMachineTypes.map(machineType => (
                  <option key={machineType} value={machineType}>
                    {machineType === 'all' ? 'All Machine Types' : machineType}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Operator:</label>
              <select 
                value={selectedOperator} 
                onChange={(e) => setSelectedOperator(e.target.value)}
                className="filter-select"
              >
                {uniqueOperators.map(operator => (
                  <option key={operator} value={operator}>
                    {operator === 'all' ? 'All Operators' : toSentenceCase(operator)}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Status:</label>
              <select 
                value={selectedStatus} 
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="started">Started</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading data...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>
              <WarningIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              {error}
            </p>
            <button onClick={fetchReportData} className="retry-btn">
              Retry
            </button>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="production-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Machine ID</th>
                  <th>Machine Type</th>
                  <th>Part</th>
                  <th className="process-column-header">Process</th>
                  <th>Operator</th>
                  <th>Completed Qty</th>
                  <th>Rejected Qty</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Time Spent</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((row, index) => (
                    <tr key={index}>
                      <td>{formatDate(row.date)}</td>
                      <td>{row.machineId}</td>
                      <td>{row.machineType || '-'}</td>
                      <td>{row.part}</td>
                      <td className="process-column">{row.process}</td>
                      <td className="operator-column">{toSentenceCase(row.operator)}</td>
                      <td>{row.completedQty}</td>
                      <td>{row.rejectedQty}</td>
                      <td>{formatTime(row.startTime)}</td>
                      <td>{formatTime(row.endTime)}</td>
                      <td>{row.totalTimeSpent}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" style={{textAlign: 'center', padding: '2rem'}}>
                      No data available {selectedMachine !== 'all' || selectedMachineType !== 'all' || selectedOperator !== 'all' || selectedStatus !== 'all' ? 'for the selected filters' : 'for the selected date'}
                    </td>
                  </tr>
                )}
                {/* Totals Row */}
                {filteredData.length > 0 && (
                  <tr className="totals-row">
                    <td colSpan="5"><strong>Total</strong></td>
                    <td></td>
                    <td><strong>{filteredTotals.completedQty}</strong></td>
                    <td><strong>{filteredTotals.rejectedQty}</strong></td>
                    <td colSpan="2"></td>
                    <td><strong>{filteredTotals.totalTimeSpent}</strong></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
          </div>
        )}

        {/* Tab Panel 1: Summary */}
        {dashboardTab === 1 && (
          <div className="attendance-dashboard-section">
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '2rem',
              paddingBottom: '1rem',
              borderBottom: '2px solid #e0e0e0'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: '#202124' }}>
                Daily Report
              </h2>
              <div style={{ fontSize: '1rem', color: '#666', fontWeight: 500 }}>
                Business Date: {dateRangeType === 'daterange' 
                  ? `${formatDate(fromDate)} - ${formatDate(toDate)}`
                  : formatDate(selectedDate)}
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading data...</p>
                </div>
              </div>
            ) : error ? (
              <div className="error-message" style={{ marginBottom: '2rem' }}>
                <p>
                  <WarningIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  {error}
                </p>
              </div>
            ) : (
              <>
                {/* Summary Cards - Mapped from getdailyreport API summary */}
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: { 
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)', 
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(5, 1fr)' 
                  },
                  gap: 2,
                  mb: 4
                }}>
                  <Card sx={{ 
                    minHeight: '120px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    color: '#fff'
                  }}>
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9 }}>
                        # Qty Packed
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '2rem' }}>
                        {reportData.totalQtyPacked || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ 
                    minHeight: '120px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    color: '#fff'
                  }}>
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9 }}>
                        # Qty Produced
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '2rem' }}>
                        {reportData.totalQtyProduced || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ 
                    minHeight: '120px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    color: '#fff'
                  }}>
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9 }}>
                        # Operators Present
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '2rem' }}>
                        {reportData.totalOperatorsPresent || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ 
                    minHeight: '120px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    color: '#fff'
                  }}>
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9 }}>
                        # Operators Absent
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '2rem' }}>
                        {reportData.totalAbsent || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ 
                    minHeight: '120px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    color: '#fff'
                  }}>
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9 }}>
                        # Total OT Hrs
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '2rem' }}>
                        {reportData.totalOTHrs || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>

                {/* Daily Attendance Sections - Mapped from attendancedashboard API */}
                {attendanceDashboardLoading ? (
                  <Box sx={{ mt: 4, textAlign: 'center', padding: '2rem' }}>
                    <div className="loading-spinner">
                      <div className="spinner"></div>
                      <p>Loading attendance data...</p>
                    </div>
                  </Box>
                ) : attendanceDashboardError ? (
                  <Box sx={{ mt: 4 }}>
                    <div className="error-message" style={{ marginBottom: '2rem' }}>
                      <p>
                        <WarningIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                        {attendanceDashboardError}
                      </p>
                    </div>
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: { 
                      xs: '1fr',
                      md: 'repeat(3, 1fr)' 
                    },
                    gap: 3,
                    mt: 4
                  }}>
                    {/* General Daily Attendance */}
                    <Card sx={{ 
                      borderRadius: '16px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      color: '#fff'
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>
                          Daily Attendance
                        </Typography>
                        <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>Total Present:</strong> {ceilNumberUi(attendanceDashboardData?.general?.totalPresent ?? attendanceDashboardData?.generalAttendance?.totalPresent ?? attendanceDashboardData?.totalPresent)}
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>Total Absent:</strong> {ceilNumberUi(attendanceDashboardData?.general?.totalAbsent ?? attendanceDashboardData?.generalAttendance?.totalAbsent ?? attendanceDashboardData?.totalAbsent)}
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>% Of Presence:</strong> {ceilPercentUi(attendanceDashboardData?.general?.percentageOfPresence ?? attendanceDashboardData?.generalAttendance?.percentageOfPresence ?? attendanceDashboardData?.percentageOfPresence)}%
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>% Of Absence:</strong> {ceilPercentUi(attendanceDashboardData?.general?.percentageOfAbsence ?? attendanceDashboardData?.generalAttendance?.percentageOfAbsence ?? attendanceDashboardData?.percentageOfAbsence)}%
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>Total Work Time Hrs:</strong> {ceilNumberUi(attendanceDashboardData?.general?.totalWorkTimeHrs ?? attendanceDashboardData?.generalAttendance?.totalWorkTimeHrs ?? attendanceDashboardData?.totalWorkTimeHrs)}
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>Total OT Hrs:</strong> {ceilNumberUi(attendanceDashboardData?.general?.totalOTHrs ?? attendanceDashboardData?.generalAttendance?.totalOTHrs ?? attendanceDashboardData?.totalOTHrs)}
                          </Box>
                          <Box component="li" sx={{ fontSize: '0.95rem' }}>
                            <strong>% Of OT:</strong> {ceilPercentUi(attendanceDashboardData?.general?.percentageOfOT ?? attendanceDashboardData?.generalAttendance?.percentageOfOT ?? attendanceDashboardData?.percentageOfOT)}%
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* CNC Daily Attendance */}
                    <Card sx={{ 
                      borderRadius: '16px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      color: '#fff'
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>
                          Daily Attendance -CNC
                        </Typography>
                        <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>Total Present:</strong> {ceilNumberUi(attendanceDashboardData?.cnc?.totalPresent ?? attendanceDashboardData?.cncAttendance?.totalPresent)}
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>Total Absent:</strong> {ceilNumberUi(attendanceDashboardData?.cnc?.totalAbsent ?? attendanceDashboardData?.cncAttendance?.totalAbsent)}
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>% Of Presence:</strong> {ceilPercentUi(attendanceDashboardData?.cnc?.percentageOfPresence ?? attendanceDashboardData?.cncAttendance?.percentageOfPresence)}%
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>% Of Absence:</strong> {ceilPercentUi(attendanceDashboardData?.cnc?.percentageOfAbsence ?? attendanceDashboardData?.cncAttendance?.percentageOfAbsence)}%
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>Total Work Time Hrs:</strong> {ceilNumberUi(attendanceDashboardData?.cnc?.totalWorkTimeHrs ?? attendanceDashboardData?.cncAttendance?.totalWorkTimeHrs)}
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>Total OT Hrs:</strong> {ceilNumberUi(attendanceDashboardData?.cnc?.totalOTHrs ?? attendanceDashboardData?.cncAttendance?.totalOTHrs)}
                          </Box>
                          <Box component="li" sx={{ fontSize: '0.95rem' }}>
                            <strong>% Of OT:</strong> {ceilPercentUi(attendanceDashboardData?.cnc?.percentageOfOT ?? attendanceDashboardData?.cncAttendance?.percentageOfOT)}%
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Manual Daily Attendance */}
                    <Card sx={{ 
                      borderRadius: '16px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      color: '#fff'
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>
                          Daily Attendance -Manual
                        </Typography>
                        <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>Total Present:</strong> {ceilNumberUi(attendanceDashboardData?.manual?.totalPresent ?? attendanceDashboardData?.manualAttendance?.totalPresent)}
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>Total Absent:</strong> {ceilNumberUi(attendanceDashboardData?.manual?.totalAbsent ?? attendanceDashboardData?.manualAttendance?.totalAbsent)}
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>% Of Presence:</strong> {ceilPercentUi(attendanceDashboardData?.manual?.percentageOfPresence ?? attendanceDashboardData?.manualAttendance?.percentageOfPresence)}%
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>% Of Absence:</strong> {ceilPercentUi(attendanceDashboardData?.manual?.percentageOfAbsence ?? attendanceDashboardData?.manualAttendance?.percentageOfAbsence)}%
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>Total Work Time Hrs:</strong> {ceilNumberUi(attendanceDashboardData?.manual?.totalWorkTimeHrs ?? attendanceDashboardData?.manualAttendance?.totalWorkTimeHrs)}
                          </Box>
                          <Box component="li" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                            <strong>Total OT Hrs:</strong> {ceilNumberUi(attendanceDashboardData?.manual?.totalOTHrs ?? attendanceDashboardData?.manualAttendance?.totalOTHrs)}
                          </Box>
                          <Box component="li" sx={{ fontSize: '0.95rem' }}>
                            <strong>% Of OT:</strong> {ceilPercentUi(attendanceDashboardData?.manual?.percentageOfOT ?? attendanceDashboardData?.manualAttendance?.percentageOfOT)}%
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </>
            )}
          </div>
        )}
      </Box>
    </div>
  );
};

export default DailyReport;

